import { Component, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription, take, tap, Observable } from 'rxjs';
import { IState } from '@core/interfaces/istate';
import { StateService } from '@core/services/state.service';
import { AddEditComponent } from '@shared/components/drdp-tables/add-edit/add-edit.component';
import { MatTableDataSource } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';
import { CustomConfigurationComponent } from './custom-configuration/custom-configuration.component';
import { SchoolYearTargetComponent } from './school-year-target/school-year-target.component';
import { ConfirmationModalComponent } from '@shared/components/confirmation-modal/confirmation-modal.component';
import { IStateSchoolYearTarget } from '@core/interfaces/istateSchoolYearTarget';
import { PermissionService } from '@core/services/permission.service';
import { IPermission } from '@core/interfaces/ipermission';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ToastService } from '@core/services/toast.service';
import { TranslocoService } from '@jsverse/transloco';
import { LookupService } from '@core/services/lookup.service';
import { RatingViewVersionDefaults } from '@core/enums/rating-view-version-defaults';
import { IRatingViewVersion } from '@core/interfaces/irating-view-version';

@Component({
    selector: 'drdp-state-configuration',
    templateUrl: './state-configuration.component.html',
    styleUrls: ['./state-configuration.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class StateConfigurationComponent implements OnInit {
  @ViewChild(AddEditComponent) table?: AddEditComponent;
  subscriptions  = new Subscription();
  title = ''
  currentState?: IState;
  tableData?: any;
  dataSource: MatTableDataSource<any> = new MatTableDataSource();
  targetTableData?: any;
  permissions: IPermission[] = [];

  tableColumns: any[] = [];

  targetColumns: any[] = [];

  constructor(
    private activatedRoute:ActivatedRoute, 
    private stateService: StateService, 
    private lookupService: LookupService,
    public modal: MatDialog,
    public dialog: MatDialog,
    private permissionService: PermissionService,
    private formBuilder: FormBuilder,
    private toastService: ToastService,
    private translocoService: TranslocoService
  ) { }

  ratingViewOptions: IRatingViewVersion[] = [];

  ratingViewsForm!: FormGroup;

  ngOnInit(): void {
    this.buildTableColumns();
    this.initializeRatingViewsForm();
    this.getRatingViewVersions().subscribe(() => {
      this.getState();
      this.getStateDashboardPermissions();
    });
  }

  buildTableColumns(): void {
    this.tableColumns = [
      { columnDef: 'demographicInformation.description', header: this.translocoService.translate('configuration.stateConfiguration.customConfig.demographicsSection'), type: 'text'},
      { columnDef: 'label', header: this.translocoService.translate('configuration.stateConfiguration.customConfig.fieldLabel'), type: 'text'},
      { columnDef: 'customFieldType.name', header: this.translocoService.translate('configuration.stateConfiguration.customConfig.fieldType'), type: 'text'},
      { columnDef: 'isActiveLabel', header: this.translocoService.translate('configuration.common.active'), type: 'text'},
      { columnDef: 'isRequired', header: this.translocoService.translate('configuration.common.required'), type: 'text'},
      { columnDef: 'options', header: this.translocoService.translate('configuration.stateConfiguration.customConfig.fieldTypeOptions'), type: 'text'},
      { columnDef: 'edit', header: this.translocoService.translate('configuration.common.edit'), type: 'edit'}
    ];

    this.targetColumns = [
      { columnDef: 'schoolYearName', header: this.translocoService.translate('configuration.common.schoolYear'), type: 'text'},
      { columnDef: 'indicator7a1', header: this.translocoService.translate('configuration.stateConfiguration.target.indicator7a1'), type: 'text', applyDecimalPipe: true},
      { columnDef: 'indicator7a2', header: this.translocoService.translate('configuration.stateConfiguration.target.indicator7a2'), type: 'text', applyDecimalPipe: true},
      { columnDef: 'indicator7b1', header: this.translocoService.translate('configuration.stateConfiguration.target.indicator7b1'), type: 'text', applyDecimalPipe: true},
      { columnDef: 'indicator7b2', header: this.translocoService.translate('configuration.stateConfiguration.target.indicator7b2'), type: 'text', applyDecimalPipe: true},
      { columnDef: 'indicator7c1', header: this.translocoService.translate('configuration.stateConfiguration.target.indicator7c1'), type: 'text', applyDecimalPipe: true},
      { columnDef: 'indicator7c2', header: this.translocoService.translate('configuration.stateConfiguration.target.indicator7c2'), type: 'text', applyDecimalPipe: true},
      { columnDef: 'edit', header: this.translocoService.translate('configuration.common.edit'), type: 'edit'},
      { columnDef: 'delete', header: this.translocoService.translate('configuration.common.delete'), type: 'delete'}
    ];
  }

  initializeRatingViewsForm(): void {
    this.ratingViewsForm = this.formBuilder.group({
      ratingViewVersion: [RatingViewVersionDefaults.VERSION_2015, Validators.required],
      showAlternateRatingViews: [false]
    });
  }

  getRatingViewVersions(): Observable<IRatingViewVersion[]> {
    return this.lookupService.getRatingViewVersions().pipe(
      take(1),
      tap((versions: IRatingViewVersion[]) => {
        this.ratingViewOptions = versions;
      })
    );
  }

  getState(): void {
    this.subscriptions.add(this.activatedRoute.params.subscribe(params => {
      let { stateId } = params;

      this.stateService.getStatesById(stateId).pipe(
        take(1),
        tap((state: IState) => {
          this.currentState = state;
          this.title = this.translocoService.translate('configuration.stateConfiguration.title', { state: this.currentState.stateName });
          
          const selectedVersion = this.ratingViewOptions.find(option => option.id === state.ratingViewVersionId);
          const versionName = selectedVersion?.versionName || RatingViewVersionDefaults.VERSION_2015;
          
          this.ratingViewsForm.patchValue({
            ratingViewVersion: versionName,
            showAlternateRatingViews: state.showAlternateRatingViews || false
          });
        })
      ).subscribe();
      this.getCustomConfigs(stateId);
      this.getTargets(stateId);
    }));
  }

  getCustomConfigs(stateId: number): void {

    this.stateService.getCustomStateConfigurations(stateId).pipe(
      take(1)
    ).subscribe((configs: any) => {
      if (configs) {
        configs.forEach((config: any) => config.isActiveLabel = config.isActive ? this.translocoService.translate('configuration.common.active') : '');
        this.tableData = configs;
        this.table?.reRender(this.tableData);
      }
    });
  }

  getStateDashboardPermissions(): void {
    this.permissionService.getStateDashboardPermissions().pipe(
      take(1)
    ).subscribe((permissions: IPermission[]) => {
      this.permissions = permissions;
    });
  }

  openModal(editData?: any, stateId?: number) {
    const modalRef = this.modal.open(CustomConfigurationComponent, { data: editData ?? stateId });

    modalRef.afterClosed().subscribe((payload: any) => {
      if (payload) {
        this.stateService.updateStateConfigurations(payload).pipe(
          take(1),
          tap(_ => {
            this.getCustomConfigs(this.currentState?.id ?? editData.data.stateId);
          })
        ).subscribe();
      }
    });
  }
  
  getTargets(stateId: number): void {
    this.stateService.getStateSchoolYearTargets(stateId).pipe(
      take(1)
    ).subscribe((targets: IStateSchoolYearTarget[]) => {
      if (targets) this.targetTableData = targets;
    });
  }

  openTargetModal(editData?: any, stateId?: number): void {
    const modalRef = this.modal.open(SchoolYearTargetComponent, { 
      data: { editData: editData?.data, stateId: stateId } 
    });
    modalRef.afterClosed().subscribe((res) => {
      if (res && this.currentState?.id) this.getTargets(this.currentState.id);
    });
  }

  deleteTargetModal(event: any): void {    
    event.data.modalInfo = {
      title: this.translocoService.translate('configuration.common.deleteTitle', { name: event.data.schoolYearName }),
      message: this.translocoService.translate('configuration.stateConfiguration.target.deleteMessage'),
      name: event.data.schoolYearName,
      primaryBtnClass: 'red',
      primaryBtnText: this.translocoService.translate('configuration.common.yes'),
      cancelText: this.translocoService.translate('configuration.common.no'),
      showCancel: true
    };
    const modalRef = this.dialog.open(ConfirmationModalComponent, {
      data: event ? event : undefined,
    });
    modalRef.afterClosed().subscribe((res) => {
      if (res) this.removeTarget(event.data.id, event.data.schoolYearName);
    });
  }

  removeTarget(id: number, name: string): void {
    this.stateService.deleteStateSchoolYearTarget(id, this.translocoService.translate('configuration.stateConfiguration.target.deletedToast', { name })).pipe(
      take(1)
    ).subscribe((res: boolean) => {
      if (res && this.currentState?.id) this.getTargets(this.currentState.id);
    });
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  saveRatingViewsConfiguration(): void {
    if (this.ratingViewsForm.invalid || !this.currentState?.id) {
      this.toastService.error(this.translocoService.translate('configuration.stateConfiguration.requiredFieldsToast'));
      return;
    }

    const formValue = this.ratingViewsForm.value;
    const selectedVersion = this.ratingViewOptions.find(option => option.versionName === formValue.ratingViewVersion);
    const versionId = selectedVersion?.id || RatingViewVersionDefaults.VERSION_2015;
    
    const updatedState = {
      ...this.currentState,
      ratingViewVersionId: versionId,
      showAlternateRatingViews: formValue.showAlternateRatingViews
    };

    this.stateService.editStates(updatedState).pipe(
      take(1)
    ).subscribe({
      next: () => {
        this.toastService.success(this.translocoService.translate('configuration.stateConfiguration.ratingViewsSavedToast'));
        this.getState();
      },
      error: () => {
        this.toastService.error(this.translocoService.translate('configuration.stateConfiguration.ratingViewsSaveFailedToast'));
      }
    });
  }
}
